import { WizardPostMessageLiterals, WizardPostMessages } from '../types/postMessageTypes';

/**
 * Environments where the products app is embedded by the local/pipeline host harness
 * (`pco-product-service-host`) rather than by CLM, so the parent origin is not `CLM_URL`.
 */
const HOST_HARNESS_ENVS = ['LOCAL', 'CI', 'CI_PREVIEW'];

export const resolveWizardTargetOrigin = (env: string, clmUrl: string): string | undefined => {
  if (HOST_HARNESS_ENVS.includes(env)) {
    return window.location.ancestorOrigins?.[0];
  }

  return clmUrl || undefined;
};

const resolveWizardAppOrigin = (): string | undefined => {
  const { origin } = new URL(import.meta.url);

  return origin.startsWith('http') ? origin : undefined;
};

export const isTrustedWizardOrigin = (env: string, origin: string, appOrigin = resolveWizardAppOrigin()): boolean => {
  if (!appOrigin) {
    return true;
  }

  return origin === appOrigin || (HOST_HARNESS_ENVS.includes(env) && origin === window.location.origin);
};

export const sendPostMessageToParent = (message: WizardPostMessageLiterals, targetOrigin: string, value?: unknown) => {
  window.parent.postMessage({ message, value }, targetOrigin);
};

const originOf = (url: string): string | undefined => {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return undefined;
  }
};

const postMessageToProductsApp = (env: string, message: WizardPostMessageLiterals, value?: unknown): void => {
  Array.from(document.querySelectorAll('iframe')).forEach((iframe) => {
    const origin = originOf(iframe.src);

    if (!origin || !isTrustedWizardOrigin(env, origin)) {
      return;
    }

    iframe.contentWindow?.postMessage({ message, value }, origin);
  });
};

export const requestWizardStatusFromProductsApp = (env: string): void => {
  postMessageToProductsApp(env, WizardPostMessages.PCO_WIZARD_STATUS_REQUEST);
};

export const openInSameTab = (url: string) => {
  const newWindow = window.open(url, '_self');
  if (newWindow) {
    newWindow.opener = null;
  }
};

export const redirectToBrServicingRolesTab = (CLM_URL: string) => {
  window.parent.postMessage({ message: 'br-servicing-roles' }, CLM_URL);
};

export const openInNewTab = (partnerUid: string | undefined, brUid: string | undefined, clmUrl: string) => {
  if (partnerUid) {
    window.parent.postMessage({ message: 'redirectToPartner', value: { partnerUid } }, clmUrl);
  } else {
    window.parent.postMessage({ message: 'newTabBrCoreData', value: { brId: brUid } }, clmUrl);
  }
};
